import React, {useCallback, useEffect, useState} from 'react';
import {
    SaleListing,
    SaleMarketplace,
    addPlacement,
    fetchListings,
    fetchMarketplaces,
    markNotSold,
    markSold,
    regenerateListing,
} from '../service/DeclutterApi';
import {UNSOLD_RESOLUTIONS} from '../support/declutterLabels';
import Spinner from './Spinner';

/**
 * Объявления о продаже.
 *
 * Цену считает backend и показывает коридором, а не точкой: точная цифра создавала бы ложное
 * впечатление о точности оценки. Тексты может подготовить модель, но если она недоступна —
 * объявление всё равно составляется шаблоном, и продажа не блокируется.
 */
function priceText(listing: SaleListing): string {
    if (!listing.priceKnown) {
        return 'Цену определить не удалось — поставьте свою';
    }
    const from = Number(listing.priceRangeFromRub ?? 0);
    const to = Number(listing.priceRangeToRub ?? 0);
    const mid = Number(listing.suggestedPriceRub ?? 0);
    return `≈ ${mid.toLocaleString('ru-RU')} ₽ (${from.toLocaleString('ru-RU')}–${to.toLocaleString('ru-RU')} ₽)`;
}

export default function SaleListings({ownerId, onChanged, listings: controlled, marketplaces: controlledPlaces}: {
    ownerId: string | null;
    onChanged?: () => void;
    /** Данные из стора страницы; без них компонент загружает объявления сам. */
    listings?: SaleListing[];
    marketplaces?: SaleMarketplace[];
}) {
    const [ownListings, setOwnListings] = useState<SaleListing[]>([]);
    const [ownPlaces, setOwnPlaces] = useState<SaleMarketplace[]>([]);
    const [loading, setLoading] = useState(controlled === undefined);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [soldPrice, setSoldPrice] = useState<Record<string, string>>({});
    const [placement, setPlacement] = useState<Record<string, string>>({});
    const [unsold, setUnsold] = useState<Record<string, string>>({});
    const listings = controlled ?? ownListings;
    const marketplaces = controlledPlaces ?? ownPlaces;

    const load = useCallback(async () => {
        if (controlled !== undefined) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const [items, places] = await Promise.all([
                fetchListings(ownerId, true),
                fetchMarketplaces(ownerId),
            ]);
            setOwnListings(items);
            setOwnPlaces(places);
        } catch {
            setError('Не удалось загрузить объявления');
        } finally {
            setLoading(false);
        }
    }, [ownerId, controlled]);

    useEffect(() => {
        void load();
    }, [load]);

    const act = async (fn: () => Promise<unknown>, message: string) => {
        setBusy(true);
        setError(null);
        try {
            await fn();
            await load();
            onChanged?.();
        } catch {
            setError(message);
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center py-8"><Spinner/></div>;
    }

    if (listings.length === 0) {
        return (
            <div className="card p-6 text-center" data-testid="sale-listings-empty">
                <p className="text-ink-700 dark:text-ink-200">Объявлений на продажу пока нет.</p>
            </div>
        );
    }

    return (
        <div className="card p-4" data-testid="sale-listings">
            <h3 className="text-sm font-medium text-ink-900 dark:text-ink-100">Объявления о продаже</h3>
            {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400" role="alert">{error}</p>}

            <ul className="mt-3 space-y-4">
                {listings.map(listing => (
                    <li key={listing.id} className="border-t border-ink-100 dark:border-ink-700 pt-3"
                        data-testid="sale-listing-row">
                        <div className="flex items-baseline justify-between gap-2 flex-wrap">
                            <span className="font-medium text-ink-900 dark:text-ink-100">
                                {listing.title ?? listing.itemName}
                            </span>
                            <span className="text-sm text-ink-600 dark:text-ink-300"
                                  data-testid="sale-listing-price">
                                {priceText(listing)}
                            </span>
                        </div>
                        {listing.description && (
                            <p className="mt-1 text-sm text-ink-600 dark:text-ink-400">
                                {listing.description}
                            </p>
                        )}
                        {listing.generatedBy === 'TEMPLATE' && (
                            <p className="mt-1 text-xs text-ink-500" data-testid="sale-listing-template">
                                Текст собран автоматически без AI — при желании отредактируйте.
                            </p>
                        )}

                        {listing.placements.length > 0 && (
                            <ul className="mt-2 flex flex-wrap gap-2">
                                {listing.placements.map(p => (
                                    <li key={p.id} className="chip">{p.marketplaceName}</li>
                                ))}
                            </ul>
                        )}

                        <div className="mt-2 flex flex-wrap items-center gap-2">
                            <select
                                className="input-field min-h-[44px] max-w-[12rem]"
                                aria-label={`Площадка для «${listing.title ?? listing.itemName}»`}
                                value={placement[listing.id] ?? ''}
                                onChange={e => {
                                    const marketplaceId = e.target.value;
                                    setPlacement(prev => ({...prev, [listing.id]: ''}));
                                    if (marketplaceId) {
                                        void act(() => addPlacement(ownerId, listing.id, marketplaceId),
                                            'Не удалось добавить площадку');
                                    }
                                }}
                            >
                                <option value="">Разместить на…</option>
                                {marketplaces.map(m => (
                                    <option key={m.id} value={m.id}>{m.name}</option>
                                ))}
                            </select>

                            <input
                                type="number"
                                min={0}
                                className="input-field min-h-[44px] max-w-[9rem]"
                                placeholder="Сумма продажи"
                                aria-label={`Сумма продажи «${listing.title ?? listing.itemName}»`}
                                value={soldPrice[listing.id] ?? ''}
                                onChange={e => setSoldPrice(prev => ({...prev, [listing.id]: e.target.value}))}
                            />
                            <button
                                type="button"
                                className="btn-accent min-h-[44px] px-4 rounded-xl"
                                data-testid={`sale-mark-sold-${listing.id}`}
                                disabled={busy || !soldPrice[listing.id]}
                                onClick={() => void act(
                                    () => markSold(ownerId, listing.id, Number(soldPrice[listing.id])),
                                    'Не удалось отметить продажу')}
                            >
                                Продано
                            </button>
                            <select
                                className="input-field min-h-[44px] max-w-[12rem]"
                                aria-label={`Если не продалось: «${listing.title ?? listing.itemName}»`}
                                data-testid={`sale-unsold-${listing.id}`}
                                value={unsold[listing.id] ?? 'DONATE'}
                                onChange={e => setUnsold(prev => ({...prev, [listing.id]: e.target.value}))}
                            >
                                {UNSOLD_RESOLUTIONS.map(r => (
                                    <option key={r.value} value={r.value}>{r.label}</option>
                                ))}
                            </select>
                            <button type="button" className="btn-secondary min-h-[44px] px-4 rounded-xl"
                                    disabled={busy}
                                    data-testid={`sale-not-sold-${listing.id}`}
                                    onClick={() => void act(
                                        () => markNotSold(ownerId, listing.id, unsold[listing.id] ?? 'DONATE'),
                                        'Не удалось изменить объявление')}>
                                Не продалось
                            </button>
                            <button type="button" className="btn-secondary min-h-[44px] px-4 rounded-xl"
                                    disabled={busy}
                                    onClick={() => void act(() => regenerateListing(ownerId, listing.id),
                                        'Не удалось обновить текст')}>
                                Пересобрать текст
                            </button>
                        </div>
                    </li>
                ))}
            </ul>
            <p className="mt-3 text-xs text-ink-500 dark:text-ink-400">
                Вещь считается проданной только после указания фактической суммы — она идёт в отчёт.
            </p>
        </div>
    );
}
